import {
  IIndirectTaxFilingV2StatusBreakdownRow,
  IIndirectTaxFilingV2StatusContributionJob,
  IIndirectTaxFilingV2StatusResponse,
} from '@commenda/api/indirect-tax/filings/types';

import { IndirectTaxRegion, IndirectTaxRegionHelper } from '../../common';
import { IIndirectTaxFiling } from '../types';
import { IndirectTaxFilingsHelper } from './IndirectTaxFilingsHelper';

/**
 * Five-state verdict surfaced by the tax-liability-report-status modal.
 * The breakdown side derives the verdict from invoice-column counters; the
 * contribution side derives it from the latest job row's `status`. Both
 * sides land on this same enum so the modal renders icons + labels
 * uniformly across tabs.
 */
export enum IndirectTaxFilingsStatusVerdict {
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  FAILED = 'failed',
  NOT_STARTED = 'not_started',
  NO_FILINGS = 'no_filings',
}

export type IndirectTaxFilingsStatusBadgeVariant =
  | 'success'
  | 'error'
  | 'blue'
  | 'gray'
  | 'warning';

/**
 * Result of slicing the dashboard's in-scope filing list into the two
 * arrays the unified status endpoint expects, plus a lookup map so column
 * renderers can join API rows back to the dashboard's IIndirectTaxFiling
 * for jurisdiction display labels (the API row carries machine names, not
 * the formatted labels the dashboard already has).
 */
export interface IndirectTaxFilingsStatusFilingSplit {
  breakdownFilingIds: string[];
  aggregateSyncFilingIds: string[];
  breakdownContributionFilingIds: string[];
  contributionFilingIds: string[];
  filingsById: Map<string, IIndirectTaxFiling>;
}

/**
 * Verdict-driven banner copy. The banner component renders the icon based
 * on `variant`; this helper owns title/description/variant so the rules
 * stay testable in plain TS.
 */
export interface IndirectTaxFilingsStatusBannerCopy {
  variant: 'success' | 'destructive' | 'warning';
  title: string;
  description: string;
}

/**
 * Display row for the US/CA breakdown tab — the API row plus the
 * dashboard-shaped filing name, jurisdiction label, formatted tax period
 * (e.g. "Mar 2026" / "Q2 2025"), and due date.
 */
export interface IndirectTaxFilingsStatusBreakdownDisplayRow
  extends IIndirectTaxFilingV2StatusBreakdownRow {
  filingNameLabel: string;
  jurisdictionLabel: string;
  taxPeriodLabel: string;
  filingDueDateLabel: string;
}

/**
 * Display row for the EU/UK contribution tab. One row per filing in
 * `contributionFilingIds`, joined to the latest sync job by entity id; if
 * no job exists, `latestSyncJob` is null and the verdict resolves to
 * NOT_STARTED.
 */
export interface IndirectTaxFilingsStatusContributionDisplayRow {
  filingId: string;
  filingNameLabel: string;
  jurisdictionLabel: string;
  taxPeriodLabel: string;
  filingDueDateLabel: string;
  latestSyncJob: IIndirectTaxFilingV2StatusContributionJob | null;
}

export class IndirectTaxFilingsStatusHelper {
  /** Regions whose filings track progress via per-invoice tax_breakdown_status. */
  static isBreakdownRegion(region: IndirectTaxRegion): boolean {
    return IndirectTaxRegionHelper.isBreakdownRegion(region);
  }

  /** Regions whose filings track progress via the FILING_CONTRIBUTION SYNC job. */
  static isContributionRegion(region: IndirectTaxRegion): boolean {
    return IndirectTaxRegionHelper.isContributionRegion(region);
  }

  /**
   * Splits the in-scope filing list by the tab that owns it and returns a
   * lookup map keyed by filing id. Each filing lands in exactly one tab:
   * breakdown-driven filings (including those whose numbers are then
   * finalised by the contribution engine) go to the breakdown tab; only
   * pure contribution filings go to the contribution tab. Within the
   * breakdown tab, `aggregateSyncFilingIds` / `breakdownContributionFilingIds`
   * record which second-stage job each filing needs. Filings whose region
   * isn't classifiable (e.g. ALL_REGIONS) are dropped — they cannot be
   * routed without a region anchor.
   */
  static splitFilingsByRegion(
    filings: ReadonlyArray<IIndirectTaxFiling>
  ): IndirectTaxFilingsStatusFilingSplit {
    const breakdownFilingIds: string[] = [];
    const aggregateSyncFilingIds: string[] = [];
    const breakdownContributionFilingIds: string[] = [];
    const contributionFilingIds: string[] = [];
    const filingsById = new Map<string, IIndirectTaxFiling>();
    filings.forEach((filing) => {
      filingsById.set(filing.id, filing);
      if (IndirectTaxRegionHelper.isBreakdownRegion(filing.region)) {
        breakdownFilingIds.push(filing.id);
        if (IndirectTaxRegionHelper.usesAggregateSync(filing.region)) {
          aggregateSyncFilingIds.push(filing.id);
        } else {
          breakdownContributionFilingIds.push(filing.id);
        }
      } else if (IndirectTaxRegionHelper.isContributionRegion(filing.region)) {
        contributionFilingIds.push(filing.id);
      }
    });
    return {
      breakdownFilingIds,
      aggregateSyncFilingIds,
      breakdownContributionFilingIds,
      contributionFilingIds,
      filingsById,
    };
  }

  /**
   * Verdict for a US/CA breakdown row. Mirrors the legacy
   * `determineFilingStatus` rules from FilingsSyncProgressModal so callers
   * land on the same answer for the same data: in-progress wins over
   * completed; "all processed but some failed" lands on FAILED;
   * "no invoices in scope" lands on NO_FILINGS.
   */
  static getBreakdownVerdict(
    row: IIndirectTaxFilingV2StatusBreakdownRow
  ): IndirectTaxFilingsStatusVerdict {
    const totalInvoices = row.totalInvoicesInRange ?? 0;
    if (totalInvoices === 0) {
      return IndirectTaxFilingsStatusVerdict.NO_FILINGS;
    }

    // Active work first — anything queued or running pins the row to
    // IN_PROGRESS regardless of how many sit in not-started.
    if (row.totalBreakdownsInProgress > 0 || row.totalBreakdownsQueued > 0) {
      return IndirectTaxFilingsStatusVerdict.IN_PROGRESS;
    }

    const processedInvoices =
      row.totalBreakdownsCompleted + row.totalBreakdownsFailed;

    // Nothing has run yet — every invoice still sits in not-started. Surface
    // NOT_STARTED so the operator can kick off the run.
    if (processedInvoices === 0) {
      return IndirectTaxFilingsStatusVerdict.NOT_STARTED;
    }

    // Partial: some invoices processed, others still pending — still in
    // progress from the operator's POV.
    if (processedInvoices !== totalInvoices) {
      return IndirectTaxFilingsStatusVerdict.IN_PROGRESS;
    }

    if (row.totalBreakdownsFailed > 0) {
      return IndirectTaxFilingsStatusVerdict.FAILED;
    }
    return IndirectTaxFilingsStatusVerdict.COMPLETED;
  }

  /**
   * Verdict for the latest FILING_CONTRIBUTION SYNC job for a filing.
   * `null` job means the sync has never been triggered → NOT_STARTED.
   * Pipeline states (PENDING / QUEUED / PROCESSING) collapse into
   * IN_PROGRESS so the badge matches the breakdown side visually.
   */
  static getContributionVerdict(
    job: IIndirectTaxFilingV2StatusContributionJob | null
  ): IndirectTaxFilingsStatusVerdict {
    if (!job) {
      return IndirectTaxFilingsStatusVerdict.NOT_STARTED;
    }
    switch (job.status) {
      case 'COMPLETED':
        return IndirectTaxFilingsStatusVerdict.COMPLETED;
      case 'FAILED':
        return IndirectTaxFilingsStatusVerdict.FAILED;
      case 'PENDING':
      case 'QUEUED':
      case 'PROCESSING':
        return IndirectTaxFilingsStatusVerdict.IN_PROGRESS;
      default:
        return IndirectTaxFilingsStatusVerdict.NOT_STARTED;
    }
  }

  static getStatusLabel(verdict: IndirectTaxFilingsStatusVerdict): string {
    switch (verdict) {
      case IndirectTaxFilingsStatusVerdict.IN_PROGRESS:
        return 'In progress';
      case IndirectTaxFilingsStatusVerdict.COMPLETED:
        return 'Completed';
      case IndirectTaxFilingsStatusVerdict.FAILED:
        return 'Failed';
      case IndirectTaxFilingsStatusVerdict.NO_FILINGS:
        return 'No invoices';
      case IndirectTaxFilingsStatusVerdict.NOT_STARTED:
      default:
        return 'Not started';
    }
  }

  static getBadgeVariant(
    verdict: IndirectTaxFilingsStatusVerdict
  ): IndirectTaxFilingsStatusBadgeVariant {
    switch (verdict) {
      case IndirectTaxFilingsStatusVerdict.COMPLETED:
        return 'success';
      case IndirectTaxFilingsStatusVerdict.FAILED:
        return 'error';
      case IndirectTaxFilingsStatusVerdict.IN_PROGRESS:
        return 'warning';
      case IndirectTaxFilingsStatusVerdict.NOT_STARTED:
        return 'blue';
      case IndirectTaxFilingsStatusVerdict.NO_FILINGS:
      default:
        return 'gray';
    }
  }

  /**
   * Format a tax period for the dashboard table: "YYYY-MM-DD — YYYY-MM-DD".
   * Both dates pass through as-is; the API already emits ISO date strings
   * the dashboard's other tables render unchanged.
   */
  static formatTaxPeriod(startDate: string, endDate: string): string {
    if (!startDate && !endDate) return '';
    if (!endDate) return startDate;
    if (!startDate) return endDate;
    return `${startDate} — ${endDate}`;
  }

  /**
   * Collapse per-filing breakdown verdicts into a single aggregate verdict
   * for the tab banner. Priority (highest wins):
   *   FAILED > IN_PROGRESS > NOT_STARTED > NO_FILINGS > COMPLETED.
   * Returns NO_FILINGS when the list is empty so the banner can show the
   * "no filings in scope" state instead of a stray COMPLETED.
   */
  static getAggregateBreakdownVerdict(
    rows: ReadonlyArray<IIndirectTaxFilingV2StatusBreakdownRow>
  ): IndirectTaxFilingsStatusVerdict {
    if (rows.length === 0) return IndirectTaxFilingsStatusVerdict.NO_FILINGS;
    return IndirectTaxFilingsStatusHelper.aggregateVerdicts(
      rows.map((row) => IndirectTaxFilingsStatusHelper.getBreakdownVerdict(row))
    );
  }

  /**
   * Collapse per-filing contribution verdicts into a single aggregate
   * verdict. Caller passes the latest sync job per filing (null when not
   * triggered yet); same priority rules as breakdown.
   */
  static getAggregateContributionVerdict(
    jobsPerFiling: ReadonlyArray<IIndirectTaxFilingV2StatusContributionJob | null>
  ): IndirectTaxFilingsStatusVerdict {
    if (jobsPerFiling.length === 0)
      return IndirectTaxFilingsStatusVerdict.NO_FILINGS;
    return IndirectTaxFilingsStatusHelper.aggregateVerdicts(
      jobsPerFiling.map((job) =>
        IndirectTaxFilingsStatusHelper.getContributionVerdict(job)
      )
    );
  }

  /**
   * Count how many filings landed on FAILED — used by the banner to surface
   * a count alongside the verdict ("3 of 12 failed").
   */
  static countFailures(
    verdicts: ReadonlyArray<IndirectTaxFilingsStatusVerdict>
  ): number {
    return verdicts.filter(
      (verdict) => verdict === IndirectTaxFilingsStatusVerdict.FAILED
    ).length;
  }

  /**
   * Verdict-driven banner copy (title + description + variant). Caller
   * picks the icon from `variant`.
   */
  static getBannerCopy(
    verdict: IndirectTaxFilingsStatusVerdict,
    filingCount: number,
    failedCount: number
  ): IndirectTaxFilingsStatusBannerCopy {
    switch (verdict) {
      case IndirectTaxFilingsStatusVerdict.COMPLETED:
        return {
          variant: 'success',
          title: 'Reports ready',
          description: `All ${filingCount} filing(s) finished processing successfully. Reports are ready to use.`,
        };
      case IndirectTaxFilingsStatusVerdict.FAILED:
        return {
          variant: 'destructive',
          title: 'Some filings failed',
          description:
            failedCount > 0
              ? `${failedCount} of ${filingCount} filing(s) failed. Re-run the job to retry the failures.`
              : 'Processing completed with errors. Re-run the job to retry.',
        };
      case IndirectTaxFilingsStatusVerdict.IN_PROGRESS:
        return {
          variant: 'warning',
          title: 'Processing in progress',
          description: `${filingCount} filing(s) still have invoices in flight. Re-check the status to see updates.`,
        };
      case IndirectTaxFilingsStatusVerdict.NOT_STARTED:
        return {
          variant: 'warning',
          title: 'No runs triggered yet',
          description: `${filingCount} filing(s) in scope have not been processed. Trigger a run to populate status.`,
        };
      case IndirectTaxFilingsStatusVerdict.NO_FILINGS:
      default:
        return {
          variant: 'warning',
          title: 'No invoices in scope',
          description:
            'Filings in scope have no invoices to process for the current period.',
        };
    }
  }

  /**
   * Build US/CA breakdown display rows from the unified status response
   * joined against the dashboard's in-scope filing map. Pure transform —
   * no React deps — so it lives on the helper rather than inline in the
   * hook.
   */
  static buildBreakdownDisplayRows(
    statusResponse: IIndirectTaxFilingV2StatusResponse | null,
    filingsById: ReadonlyMap<string, IIndirectTaxFiling>
  ): IndirectTaxFilingsStatusBreakdownDisplayRow[] {
    const rows = statusResponse?.breakdown?.filings ?? [];
    return rows.map((row) => {
      const dashboardFiling = filingsById.get(row.filingId);
      return {
        ...row,
        filingNameLabel:
          row.filingName || dashboardFiling?.returnName || row.filingId,
        jurisdictionLabel:
          dashboardFiling?.jurisdiction || row.state || row.country,
        taxPeriodLabel: IndirectTaxFilingsHelper.formatFilingPeriod(
          row.taxPeriodStartDate,
          row.filingFrequency
        ),
        filingDueDateLabel: dashboardFiling?.dueDate || row.filingDueDate,
      };
    });
  }

  /**
   * Build EU/UK contribution display rows. One row per filing in
   * `contributionFilingIds`; latest FILING_CONTRIBUTION SYNC job is joined
   * by entity id (filing id). Defensive first-wins on duplicate entity ids
   * since the upstream DISTINCT ON already picks the latest per filing.
   */
  static buildContributionDisplayRows(
    statusResponse: IIndirectTaxFilingV2StatusResponse | null,
    contributionFilingIds: ReadonlyArray<string>,
    filingsById: ReadonlyMap<string, IIndirectTaxFiling>
  ): IndirectTaxFilingsStatusContributionDisplayRow[] {
    if (contributionFilingIds.length === 0) return [];
    const jobs = statusResponse?.contribution?.jobs ?? [];
    const latestSyncJobByFilingId = new Map<
      string,
      IIndirectTaxFilingV2StatusContributionJob
    >();
    jobs.forEach((job) => {
      if (!latestSyncJobByFilingId.has(job.entityId)) {
        latestSyncJobByFilingId.set(job.entityId, job);
      }
    });
    return contributionFilingIds.map((filingId) => {
      const dashboardFiling = filingsById.get(filingId);
      return {
        filingId,
        filingNameLabel: dashboardFiling?.returnName || filingId,
        jurisdictionLabel: dashboardFiling?.jurisdiction || '',
        taxPeriodLabel: dashboardFiling?.filingPeriod || '',
        filingDueDateLabel: dashboardFiling?.dueDate || '',
        latestSyncJob: latestSyncJobByFilingId.get(filingId) ?? null,
      };
    });
  }

  private static aggregateVerdicts(
    verdicts: ReadonlyArray<IndirectTaxFilingsStatusVerdict>
  ): IndirectTaxFilingsStatusVerdict {
    if (verdicts.length === 0)
      return IndirectTaxFilingsStatusVerdict.NO_FILINGS;
    if (verdicts.some((v) => v === IndirectTaxFilingsStatusVerdict.FAILED))
      return IndirectTaxFilingsStatusVerdict.FAILED;
    if (verdicts.some((v) => v === IndirectTaxFilingsStatusVerdict.IN_PROGRESS))
      return IndirectTaxFilingsStatusVerdict.IN_PROGRESS;
    if (verdicts.some((v) => v === IndirectTaxFilingsStatusVerdict.NOT_STARTED))
      return IndirectTaxFilingsStatusVerdict.NOT_STARTED;
    if (verdicts.every((v) => v === IndirectTaxFilingsStatusVerdict.NO_FILINGS))
      return IndirectTaxFilingsStatusVerdict.NO_FILINGS;
    return IndirectTaxFilingsStatusVerdict.COMPLETED;
  }
}
